import type { MouseEventHandler } from 'react'
import React, { useCallback, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import NavigationNavItem from './NavigationNavItem'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import { useScreenSizeOnly } from 'hooks/useScreenSize'

interface Props {
  currentActiveNavItem?: App.NavItem
  isOpen: boolean
  navItem: App.NavItem
  onClose: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
  onSubNavClick: (selection: string, position: number) => void
  onToggle: (navItem: App.NavItem) => void
  position: number
}

function NavigationNavDropdownItem(props: Props) {
  const {
    currentActiveNavItem,
    isOpen,
    navItem,
    onClose,
    onSubNavClick,
    onToggle,
    position,
  } = props

  const navItemRef = useRef<HTMLButtonElement>(null)
  const isMobile = useScreenSizeOnly('mobile')
  const intl = useIntl()

  const handleToggle = useCallback(() => {
    onToggle(navItem)
  }, [navItem, onToggle])

  const handleSubNavClick = useCallback((e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>, subNavPosition: number) => {
    onSubNavClick(e.currentTarget.id, position + subNavPosition)
    // after use clicks and navigates, design wants our 'active' indicator, not our focus state
    e.currentTarget.blur()
  }, [onSubNavClick, position])

  const newLabelText = useMemo(() => intl.formatMessage({
    id: 'ULXt7xsBZx',
    defaultMessage: 'NEW',
    description: 'New badge label for navigation items in the site header',
  }), [intl])

  const Content = useMemo(() => navItem.items?.map((item, index) => (
    <ClickableListItem
      size={isMobile ? 'large' : 'medium'}
      key={item.id}
      id={item.id}
      aria-label={intl.formatMessage(
        {
          id: 'a6lcbHs0TS',
          defaultMessage: 'Select {itemText}',
          description: 'Accessibility label for selecting a navigation item',
        },
        { itemText: item.text },
      )}
      onClick={(e) => handleSubNavClick(e, index)}
      to={item.to}
      href={item.href}
      title={item.text}
      target={item.target}
      className={item.linkClass}
      endIcon={item.newLabel && <StatusLabel variant="confirmation">{newLabelText}</StatusLabel>}
      selected={item === currentActiveNavItem}
    />
  )), [currentActiveNavItem, handleSubNavClick, navItem.items, isMobile, intl, newLabelText])

  return <>
    <NavigationNavItem
      ref={navItemRef}
      navItem={navItem}
      onClick={handleToggle}
      open={isOpen}
      active={navItem === currentActiveNavItem}
      position={position}
    />

    <CSSBreakpoint max="mobile">
      <ModalBase isOpen={isOpen && isMobile} onClose={onClose}>
        <ModalHeader
          title={intl.formatMessage({
            id: 'oK5KuccwLE',
            defaultMessage: 'More',
            description: 'Navigation link label for the more dropdown in the site header',
          })}
          onCloseButtonClick={onClose}
        />
        <ModalBody>
          <ModalContent>
            {Content}
          </ModalContent>
        </ModalBody>
      </ModalBase>
    </CSSBreakpoint>

    <CSSBreakpoint min="desktop">
      <DropdownList
        size="S"
        anchorRef={navItemRef}
        triggerRef={navItemRef}
        open={isOpen}
        onClose={onClose}
      >
        {Content}
      </DropdownList>
    </CSSBreakpoint>
  </>
}

export default NavigationNavDropdownItem
